import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Video, Loader2, Plus, X as XIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export default function MeetingDialog({ open, onOpenChange, deal }) {
  const qc = useQueryClient();
  const now = new Date();
  const defaultDay = now.toISOString().slice(0, 10);
  const defaultStart = "10:00";
  const [title, setTitle] = useState(deal ? `Mediana · ${deal.clientName}` : "");
  const [day, setDay] = useState(defaultDay);
  const [start, setStart] = useState(defaultStart);
  const [duration, setDuration] = useState(30);
  const [emails, setEmails] = useState(deal?.contactEmail ? [deal.contactEmail] : []);
  const [emailDraft, setEmailDraft] = useState("");
  const [description, setDescription] = useState("");

  const addEmail = () => {
    const e = emailDraft.trim();
    if (!e || emails.includes(e)) return;
    if (!/.+@.+\..+/.test(e)) { toast.error("Correo inválido"); return; }
    setEmails([...emails, e]); setEmailDraft("");
  };

  const create = useMutation({
    mutationFn: async () => {
      const startIso = new Date(`${day}T${start}:00`).toISOString();
      const endIso = new Date(new Date(startIso).getTime() + duration * 60_000).toISOString();
      return (await api.post(`/deals/${deal.id}/meeting`, { title, start: startIso, end: endIso, attendees: emails, description })).data;
    },
    onSuccess: (m) => {
      toast.success("Reunión agendada en Google Meet");
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      if (m?.meetLink) window.open(m.meetLink, "_blank", "noopener,noreferrer");
      onOpenChange(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!deal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="meeting-dialog">
        <DialogHeader>
          <DialogTitle>Agendar reunión con {deal.clientName}</DialogTitle>
          <DialogDescription>Se crea un evento en tu Calendar con enlace de Google Meet y se avisa a los invitados por correo.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Título</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="meeting-title-input" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="grid gap-1.5"><Label className="text-xs">Día</Label><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="meeting-day-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Hora</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="meeting-time-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Duración (min)</Label><Input type="number" min="15" step="15" value={duration} onChange={(e) => setDuration(Number(e.target.value) || 30)} data-testid="meeting-duration-input" /></div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Invitados</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="meeting-attendees">
              {emails.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px]">
                  {e}
                  <button onClick={() => setEmails(emails.filter((x) => x !== e))} aria-label="Quitar"><XIcon className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <Input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} onKeyDown={(e) => (e.key === "Enter" ? addEmail() : null)} placeholder="correo@dominio.com" data-testid="meeting-email-draft" />
              <Button variant="outline" onClick={addEmail} data-testid="meeting-email-add"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Agenda / notas</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="meeting-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} disabled={!title.trim() || create.isPending} data-testid="meeting-create-button">
            {create.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Video className="mr-1.5 h-4 w-4" />} Crear y enviar invitaciones
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
